import * as React from 'react';
import { useSelector } from 'react-redux';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import useDebouncedState from 'hooks/useDebouncedState';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { PrimaryButton } from '@fluentui/react';
import { SelectOption } from 'formComponents/Select/types';
import { ValueType } from 'react-select';
import { Select } from 'formComponents/Select/Select';
import { useBetreuer, useBetreuerByNr } from 'api/betreuer';
import { authSelector } from 'store/auth/selector';
import { Label } from '@fluentui/react/lib/Label';
import { AuftragStatus } from 'formComponents/lookups/AuftragStatus';
import { generateReportPrintUrl } from '../utils';
import { defaultFilterValues, FilterValues, Berechnungsart } from './types';
import { OffeneAuftraege } from './OffeneAuftraege';

const styles = mergeStyleSets({
    container: {
        margin: 0,
        marginTop: 5,
        padding: 0,
        display: 'grid',
        gridTemplateRows: 'max-content 1fr',
        gridRowGap: 5,
        height: 'calc(100vh - 75px)',
    },
    searchLine1: {
        display: 'grid',
        gridTemplateColumns: '180px 180px 180px 180px 1fr max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    actionButtons: {
        marginTop: 29,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, max-content)',
        gridColumnGap: 10,
    },
    reset: {
        textAlign: 'right',
        marginTop: 8,
    },
});

const alleBetreuerOption = { value: '__all__', label: 'Alle Betreuer' };

const berechnungsartOptions = [
    {
        value: 'offen',
        label: 'Offen',
    },
    {
        value: 'status',
        label: 'Status',
    },
];

export const OffeneAuftraegeView: React.FC = () => {
    const auth = useSelector(authSelector);
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const betreuer = useBetreuer();
    const betreuerByNr = useBetreuerByNr();
    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>(
        {
            ...defaultFilterValues,
        },
        2000,
    );

    const betreuerFilterOptions = React.useMemo<SelectOption[]>(() => {
        const options: SelectOption[] = [];

        if (auth.rolle === 'AD') {
            options.push({
                value: auth.nr?.toString() || '',
                label: auth.name || '?',
            });
        } else {
            options.push(alleBetreuerOption);
            betreuer
                .filter((b) => b.aktiv && b.name)
                .sort((a, b) => a.name.localeCompare(b.name))
                .forEach((b) => options.push({ value: b.nr.toString(), label: b.name }));
        }

        return options;
    }, [auth, betreuer]);

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <div className={styles.container}>
            <div className={styles.searchLine1}>
                <div>
                    <Label>Betreuer (Auftrag)</Label>
                    <Select
                        name="betreuer"
                        value={
                            filterValues.betreuer
                                ? {
                                      value: filterValues.betreuer.toString(),
                                      label: betreuerByNr[filterValues.betreuer]?.name,
                                  }
                                : alleBetreuerOption
                        }
                        options={betreuerFilterOptions}
                        onChange={(newValue: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                betreuer:
                                    newValue?.value && newValue?.value !== '__all__'
                                        ? parseInt(newValue?.value, 10)
                                        : undefined,
                            });
                        }}
                    />
                </div>

                <div>
                    <Label>Tel. Betreuer (Auftrag)</Label>
                    <Select
                        name="telBetreuer"
                        value={
                            filterValues.telBetreuer
                                ? {
                                      value: filterValues.telBetreuer.toString(),
                                      label: betreuerByNr[filterValues.telBetreuer]?.name,
                                  }
                                : alleBetreuerOption
                        }
                        options={betreuerFilterOptions}
                        onChange={(newValue: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                telBetreuer:
                                    newValue?.value && newValue?.value !== '__all__'
                                        ? parseInt(newValue?.value, 10)
                                        : undefined,
                            });
                        }}
                    />
                </div>

                <div>
                    <Label>Status</Label>
                    <AuftragStatus
                        renderLabel="no"
                        addAll
                        value={filterValues.status?.toString() || '__all__'}
                        onChange={(newValue): void => {
                            setFilterValues({
                                ...filterValues,
                                status: newValue === '__all__' ? undefined : parseInt(newValue, 10),
                            });
                        }}
                    />
                </div>

                <div>
                    <Label>Berechnungsart</Label>
                    <Select
                        name="status"
                        value={berechnungsartOptions.find((o) => o.value === filterValues.berechnungsart)}
                        options={berechnungsartOptions}
                        onChange={(option: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                berechnungsart: (option?.value || 'offen') as Berechnungsart,
                            });
                        }}
                    />
                </div>

                <div />

                <div className={styles.actionButtons}>
                    {auth.rolle !== 'AD' && auth.rolle !== 'EX' && (
                        <PrimaryButton
                            onClick={() => {
                                window.open(
                                    generateReportPrintUrl('offeneauftraege', {
                                        sortColumn: filterValues.sortColumn,
                                        sortDirection: filterValues.sortDirection,
                                        betreuer: filterValues.betreuer,
                                        telBetreuer: filterValues.telBetreuer,
                                        status: filterValues.status,
                                    }),
                                );
                            }}
                            text="Drucken"
                        />
                    )}
                </div>
            </div>

            <OffeneAuftraege
                sortColumn={filterValues.sortColumn}
                sortDirection={filterValues.sortDirection}
                toggleSort={(sortColumn) => {
                    if (filterValues.sortColumn === sortColumn) {
                        setFilterValues({
                            ...filterValues,
                            sortColumn,
                            sortDirection: filterValues.sortDirection === 'asc' ? 'desc' : 'asc',
                        });
                    } else {
                        setFilterValues({ ...filterValues, sortColumn, sortDirection: 'desc' });
                    }
                }}
                betreuer={debouncedFilterValues.betreuer}
                telBetreuer={debouncedFilterValues.telBetreuer}
                status={debouncedFilterValues.status}
                berechnungsart={debouncedFilterValues.berechnungsart}
            />
        </div>
    );
};
